import { useEffect, useState } from "react";
import api from "../../api/axiosConfig";
import { format } from "date-fns";
import { GoDownload } from "react-icons/go";
import "../../styles/Settings/VariationForClient.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { Helmet } from "react-helmet";
const VariationForClient = () => {
  const [variation, setVariation] = useState(null);
  const [loading, setLoading] = useState(true);
  const { variationId, token } = useParams();
  const [error, setError] = useState("");
  const [companyDetails, setCompanyDetails] = useState({
    name: "",
    companyregno: "",
    contactno: "",
    email: "",
    hourrate: "",
    website: "",
    logo: "",
    address: "",
    about: "",
    social: {
      facebook: "",
      twitter: "",
      linkedin: "",
    },
  });

  const hourlyRate = variation?.hourrate;

  const convertTimeToDecimal = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    return hours + minutes / 60 + seconds / 3600;
  };

  const totalAmount = variation
    ? (convertTimeToDecimal(variation.plannedhours) * hourlyRate).toFixed(2)
    : "0.00";

  const fetchVariationDetails = async () => {
    try {
      const res = await api.get(
        `/api/variation/approval/details/${variationId}/${token}`
      );
      const companyRes = await api.get("/api/company-settings");
      if (companyRes.data) {
        setCompanyDetails((prev) => ({
          ...prev,
          ...companyRes.data,
          social: {
            facebook: companyRes.data?.social?.facebook || "",
            twitter: companyRes.data?.social?.twitter || "",
            linkedin: companyRes.data?.social?.linkedin || "",
          },
        }));
      }
      setVariation(res.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching variation", err);
      setError("Error fetching variation");
    }
  };

  useEffect(() => {
    fetchVariationDetails();
  }, []);

  const handleAccept = async (variationId, decision) => {
    try {
      setLoading(true);

      const requestData = {
        variationId,
        token,
        decision,
      };

      const response = await api.post(
        "/api/variation/approval/submit",
        requestData
      );
      setVariation((prev) => ({
        ...prev,
        variation_status: requestData,
      }));
      if (response.status === 200) {
        toast.success("Variation status updated successfully", {
          position: "bottom-center",
          autoClose: 2000,
        });
      } else {
        toast.error("Failed to update status variation", {
          position: "bottom-center",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error updating variation:", error);
      toast.error("Error occurred while updating variation", error, {
        position: "bottom-center",
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!variation)
    return <div className="vo-loading">Invalid or expired token</div>;

  const handleDownload = async () => {
    const doc = new jsPDF({
      orientation: "landspace",
      unit: "mm",
      format: "a4",
    });

    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;

    const rate = parseFloat(hourlyRate) || 0;
    const convertTimeToDecimal = (timeStr) => {
      const [h = 0, m = 0, s = 0] = timeStr.split(":").map(Number);
      return h + m / 60 + s / 3600;
    };
    const hours = convertTimeToDecimal(variation.plannedhours);
    const amount = (rate * hours).toFixed(2);

    // Draw border
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.rect(margin, margin, contentWidth, pageHeight - margin * 2);

    const { name, companyregno, address, email, website, logo, contactno } =
      companyDetails;

    // Add logo if it was successfully converted
    const logoHeight = 20;
    const logoY = margin + 2;

    // Company Info
    const companyLines = [
      name || "Company Name",
      ...address.split(",").map((line) => line.trim()),
      email && ` ${email}`,
      website && ` ${website}`,
      companyregno && `Company Reg. No.: ${companyregno}`,
    ].filter(Boolean);

    doc.setFontSize(8);
    const companyStartY = margin + 6;
    companyLines.forEach((line, i) => {
      doc.text(line, pageWidth - margin - 2, companyStartY + i * 5, {
        align: "right",
      });
    });

    // Calculate bottom of logo and bottom of company info
    const companyEndY = companyStartY + companyLines.length * 5;
    const logoBottomY = logoY + logoHeight;

    // Choose whichever is lower, and add spacing
    let cursorY = Math.max(companyEndY, logoBottomY) + 10;

    // Bill Info
    const billLeftX = margin + 4;
    const billRightX = pageWidth / 2 + 100;

    doc.setFontSize(8);
    doc.text(`Bill To: ${variation.customer_name}`, billLeftX, cursorY);

    doc.text(
      `Date: ${format(new Date(variation.created_at), "dd-MM-yyyy")}`,
      billRightX,
      cursorY
    );
    doc.text(
      `Expiry: ${format(new Date(variation.expiry_date), "dd-MM-yyyy")}`,
      billRightX,
      cursorY + 5
    );
    doc.text(`Project: ${variation.project_name}`, billRightX, cursorY + 10);
    doc.text(`Phase: ${variation.phase_title}`, billRightX, cursorY + 15);

    // Add bottom margin after bill info
    cursorY += 25;

    // Table
    const formattedName = variation.name;

    const formattedDesc = variation.description;

    autoTable(doc, {
      startY: cursorY,
      head: [
        ["#", "VO No", "Item", "Description", "Total PHrs", "Rate", "Amount"],
      ],
      body: [
        [
          "1",
          variation.variationno,
          formattedName,
          formattedDesc,
          variation.plannedhours_formatted || variation.plannedhours,
          `${variation.currency_symbol}${rate}`,
          `${variation.currency_symbol}${amount}`,
        ],
      ],
      margin: { left: margin + 5, right: margin + 5 },
      styles: { fontSize: 10, cellPadding: 2 },
    });

    // Footer
    doc.setFontSize(8);
    doc.text(
      "This is a system-generated PDF",
      pageWidth / 2,
      pageHeight - margin + 3,
      {
        align: "center",
      }
    );

    doc.save(`Variation-${variation.variationno || "Order"}.pdf`);
  };

  return (
    <div>
      <Helmet>
        <title>MSES Group Ltd</title>
      </Helmet>
      <ToastContainer />
      <div className="company-container">
        <h3 className="company-name-header">{companyDetails.name}</h3>
      </div>
      <div className="vo-scrolling-container">
        {variation.variation_status === "Confirmed" && (
          <div className="vo-actions">
            <button
              onClick={() => handleAccept(variationId, "Approved")}
              className="btn accept"
            >
              Approve
            </button>
            <button
              onClick={() => handleAccept(variationId, "Rejected")}
              className="btn reject"
            >
              Decline
            </button>
            <button onClick={handleDownload} className="btn download">
              <GoDownload />
            </button>
          </div>
        )}
        <div
          className="vo-container"
          style={{
            top: variation.variation_status === "Confirmed" ? "0rem" : "3rem",
          }}
        >
          <div class="ribbon">
            <div class="ribbon-inner ribbon-open">
              {variation.variation_status === "Confirmed" ? "Open" : "Closed"}
            </div>
          </div>
          <div className="header-container">
            <img
              src={companyDetails.logo}
              className="mses-logo"
              alt="MSES-logo"
            />
            <ul>
              <li>
                <strong>{companyDetails.name}</strong>
              </li>
              <li>
                {companyDetails.address.split(",").map((line, index) => (
                  <div key={index}>{line.trim()}</div>
                ))}
              </li>
              <li>{companyDetails.email}</li>
              <li>{companyDetails.website}</li>
              <li>Company Reg. No. {companyDetails.companyregno}</li>
            </ul>
          </div>

          <div className="vo-details">
            <div className="vo-row">
              <span className="vo-details-span">
                <strong>Bill To </strong> {variation.customer_name}
              </span>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "left",
                  gap: "0.25rem",
                }}
              >
                <span className="vo-details-span">
                  <strong>Bill Date</strong>{" "}
                  {format(new Date(variation.created_at), "dd-MM-yyyy")}
                </span>
                <span className="vo-details-span">
                  <strong>Expiry Date</strong>{" "}
                  {format(new Date(variation.expiry_date), "dd-MM-yyyy")}
                </span>
                <span className="vo-details-span">
                  {" "}
                  <strong>Project</strong> {variation.project_name}
                </span>
                <span className="vo-details-span">
                  <strong>Phase</strong> {variation.phase_title}
                </span>
              </div>
            </div>

            <div className="vo-divider" />

            <div className="vo-grid">
              <table className="vo-approval-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Vo No</th>
                    <th>Item</th>
                    <th>Description</th>
                    <th>Total PHrs</th>
                    <th style={{ textWrap: "nowrap" }}>Rate</th>
                    <th style={{ textWrap: "nowrap" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>{variation.variationno}</td>
                    <td style={{ textWrap: "wrap", width: "450px" }}>
                      {variation.name}
                    </td>
                    <td style={{ textWrap: "wrap", width: "450px" }}>
                      {variation.description}
                    </td>
                    <td>{variation.plannedhours_formatted}</td>
                    <td>
                      {variation.currency_symbol}
                      {hourlyRate}
                    </td>
                    <td>
                      {variation.currency_symbol}
                      {totalAmount}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {error && <p className="vo-error">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default VariationForClient;
